// <s-bike-finder> — cascading brand > model > generation > category selects.
// Reads its dataset from a child script[data-bike-finder-data] and the search route from data-search-url.
// Menu-backed models link to their collection; a chosen category narrows it with the native /collections/<handle>/<tag> filter.
// Models without a URL (fallback list) route to /collections/all/<brand>+<model>[+<category>] so customers only see products tagged for that bike.
// The rider's bike (brand/model/gen titles) is remembered on this device in localStorage and restored on later visits.
// The sticky bar can be dismissed; html[data-finder-closed] hides it (set early by an inline head script to avoid a flash).

const BIKE_KEY = 'pl-my-bike';
const BAR_KEY = 'pl-finder-bar';
const CHANGE_EVENT = 'bike-finder:change';

// Mirrors Liquid's handleize so "Sur-Ron" -> "sur-ron" and "Light Bee X" -> "light-bee-x".
const handleize = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const store = {
  get(key) {
    try { return localStorage.getItem(key); } catch { return null; }
  },
  set(key, value) {
    try { localStorage.setItem(key, value); } catch { /* storage unavailable: feature degrades silently */ }
  },
  remove(key) {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  },
};

export function getSavedBike() {
  try {
    const bike = JSON.parse(store.get(BIKE_KEY) || 'null');
    return bike && bike.brand ? bike : null;
  } catch {
    return null;
  }
}

function bikeLabel(bike) {
  return bike ? [bike.brand, bike.model, bike.gen].filter(Boolean).join(' ') : '';
}

function saveBike(bike) {
  if (bike) store.set(BIKE_KEY, JSON.stringify(bike));
  else store.remove(BIKE_KEY);
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { bike } }));
}

// Reopen controls and "My bike" labels outside the finder (header, footer).
function syncLabels() {
  const label = bikeLabel(getSavedBike());
  document.querySelectorAll('[data-my-bike-label]').forEach((el) => {
    const template = el.dataset.savedTemplate || '';
    const fallback = el.dataset.emptyLabel || '';
    el.textContent = label && template ? template.replace('[bike]', label) : fallback;
  });
}

function reopenBar() {
  store.remove(BAR_KEY);
  delete document.documentElement.dataset.finderClosed;
  const bar = document.getElementById('shop-by-bike-bar');
  if (!bar) return false;
  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    bar.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: 'ease-out' });
  }
  bar.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  bar.querySelector('[data-finder-brand]')?.focus({ preventScroll: true });
  return true;
}

class SBikeFinder extends HTMLElement {
  #ac;
  #data = { brands: [], categories: [] };
  #restoring = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const raw = this.querySelector('script[data-bike-finder-data]');
    try {
      this.#data = JSON.parse(raw?.textContent || '{}');
    } catch {
      this.#data = { brands: [], categories: [] };
    }
    this.#data.brands ||= [];
    this.#data.categories ||= [];
    this.#data.available ||= [];

    this.brand = this.querySelector('[data-finder-brand]');
    this.model = this.querySelector('[data-finder-model]');
    this.gen = this.querySelector('[data-finder-gen]');
    this.genWrap = this.querySelector('[data-finder-gen-wrap]');
    this.category = this.querySelector('[data-finder-category]');
    this.forget = this.querySelector('[data-finder-forget]');
    this.form = this.querySelector('form');
    if (!this.brand || !this.model || !this.form) return;

    this.#fill(this.brand, this.#data.brands);
    this.#fill(this.category, this.#data.categories);
    this.#fill(this.model, []);
    this.model.disabled = true;
    this.#syncGen();
    this.#restore(getSavedBike());

    this.brand.addEventListener('change', () => {
      this.#syncModels();
    }, { signal });
    this.model.addEventListener('change', () => this.#syncGen(), { signal });
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.#remember();
      const url = this.#destination();
      if (url) window.location.assign(url);
    }, { signal });

    this.forget?.addEventListener('click', () => {
      saveBike(null);
      this.brand.focus();
    }, { signal });

    this.querySelector('[data-finder-close]')?.addEventListener('click', () => this.#dismiss(), { signal });

    // Keep every finder on the page (bar + homepage panel) in step with the saved bike.
    window.addEventListener(CHANGE_EVENT, (e) => {
      if (e.detail?.source === this) return;
      this.#restore(e.detail?.bike || null, true);
    }, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #dismiss() {
    store.set(BAR_KEY, 'closed');
    const done = () => { document.documentElement.dataset.finderClosed = ''; };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return done();
    this.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160, easing: 'ease-in' }).finished.then(done, done);
    // Move focus somewhere sensible so keyboard users are not stranded on a hidden control.
    document.querySelector('[data-finder-open]')?.focus({ preventScroll: true });
  }

  #remember() {
    const { brand, model, gen } = this.#current();
    if (!brand) return;
    const bike = { brand: brand.t, model: model?.t || '', gen: gen?.t || '' };
    store.set(BIKE_KEY, JSON.stringify(bike));
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { bike, source: this } }));
  }

  #restore(bike, clearIfEmpty = false) {
    if (!bike) {
      if (clearIfEmpty) {
        this.brand.value = '';
        this.#syncModels();
      }
      this.#syncForget(null);
      return;
    }
    const bi = this.#data.brands.findIndex((b) => b.t === bike.brand);
    if (bi < 0) {
      this.#syncForget(null);
      return;
    }
    this.brand.value = String(bi);
    this.#syncModels();
    const models = this.#data.brands[bi].m || [];
    const mi = models.findIndex((m) => m.t === bike.model);
    if (mi >= 0) {
      this.model.value = String(mi);
      this.#syncGen();
      const gens = models[mi].g || [];
      const gi = gens.findIndex((g) => g.t === bike.gen);
      if (gi >= 0 && this.gen) this.gen.value = String(gi);
    }
    this.#syncForget(bike);
  }

  #syncForget(bike) {
    if (this.forget) this.forget.hidden = !bike;
  }

  #syncModels() {
    const b = this.#current().brand;
    this.#fill(this.model, b?.m || []);
    this.model.disabled = !b;
    this.#syncGen();
  }

  #current() {
    const brand = this.#data.brands[Number(this.brand.value === '' ? NaN : this.brand.value)];
    const model = brand?.m?.[Number(this.model.value === '' ? NaN : this.model.value)];
    const gen = model?.g?.[Number(this.gen?.value === '' ? NaN : this.gen?.value)];
    const category = this.#data.categories[Number(this.category?.value === '' ? NaN : this.category?.value)];
    return { brand, model, gen, category };
  }

  #fill(select, items) {
    if (!select) return;
    const placeholder = select.querySelector('option[value=""]');
    select.replaceChildren(...(placeholder ? [placeholder] : []));
    items.forEach((item, i) => {
      const opt = document.createElement('option');
      opt.value = String(i);
      opt.textContent = item.t;
      select.append(opt);
    });
    select.value = '';
  }

  #syncGen() {
    if (!this.gen) return;
    const gens = this.#current().model?.g || [];
    this.#fill(this.gen, gens);
    this.gen.disabled = gens.length === 0;
    this.genWrap?.toggleAttribute('data-empty', gens.length === 0);
  }

  #destination() {
    const { brand, model, gen, category } = this.#current();
    const searchUrl = this.dataset.searchUrl;
    const target = gen?.u || model?.u || (!model ? brand?.u : '');

    if (target && target.includes('/collections/')) {
      const base = target.split('?')[0].replace(/\/$/, '');
      return category?.tag ? `${base}/${encodeURIComponent(category.tag)}` : base;
    }
    if (!brand && category?.u) return category.u;

    // Fallback list: filter All products by exact product tags (brand + model + gen + category), never loose search.
    const collectionUrl = (this.dataset.collectionUrl || '').replace(/\/$/, '');
    const tags = [brand?.t, model?.t, gen?.t].filter(Boolean).map(handleize);
    if (category?.tag) tags.push(category.tag);
    // Shopify drops filter tags no product carries (showing unrelated parts), so send combos with an unused tag to the "no parts yet" view.
    const available = new Set(this.#data.available);
    if (tags.length && tags.some((tag) => !available.has(tag)) && this.dataset.noPartsUrl) return this.dataset.noPartsUrl;
    if (collectionUrl && tags.length) return `${collectionUrl}/${tags.map(encodeURIComponent).join('+')}`;

    const terms = [brand?.t, model?.t, gen?.t, category?.t].filter(Boolean).join(' ');
    if (!terms || !searchUrl) return target || '';
    const params = new URLSearchParams({ q: terms, type: 'product', 'options[prefix]': 'last' });
    return `${searchUrl}?${params.toString()}`;
  }
}

if (!customElements.get('s-bike-finder')) {
  customElements.define('s-bike-finder', SBikeFinder);

  // Any [data-finder-open] control (header "My bike" button, footer link) brings the bar back.
  document.addEventListener('click', (e) => {
    const opener = e.target instanceof Element ? e.target.closest('[data-finder-open]') : null;
    if (!opener) return;
    if (reopenBar()) e.preventDefault();
  });
  window.addEventListener(CHANGE_EVENT, syncLabels);
  syncLabels();
}
